<template>
  <div class="profile-select">
    <v-autocomplete
      :id="transactionPartyType"
      ref="profileSelect"
      :value="value"
      :menu-props="{contentClass: 'profile-select--menu v-autocomplete__content--cards'}"
      :items="options"
      :item-text="getItemText"
      item-value="id"
      :loading="loading"
      :label="label"
      placeholder="Name, Surname, Company name, IBAN, Account Nr., Reg nr."
      :search-input.sync="search"
      no-filter
      return-object
      :prepend-icon="hideIcon ? undefined : 'search'"
      :disabled="disabled"
      :clearable="clearable"
      :error-messages="getValidationErrorMessage(transactionPartyType)"
      @input="onProfileSelect"
    >
      <template
        v-if="iconsVisible"
        slot="append"
      >
        <v-tooltip
          v-for="(icon, key) in icons"
          :key="key"
          bottom
        >
          <v-icon
            slot="activator"
            :class="`${icon.name}-icon`"
          >{{ icon.name }}</v-icon>
          <span>{{ icon.tooltip }}</span>
        </v-tooltip>
      </template>
      <template
        slot="item"
        slot-scope="data"
      >
        <template>
          <v-list-tile-avatar>
            <img
              v-if="data.item.hasProfilePicture"
              :src="data.item.pictureImageUrl"
              alt=""
            >
            <v-icon
              v-else
              class="grey lighten-2 grey--text text--darken-4"
              medium
            >person</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title :inner-html.prop="getItemText(data.item) | searchHighlight(search)" />
            <v-list-tile-sub-title>{{ data.item.regNumber }}</v-list-tile-sub-title>
          </v-list-tile-content>
        </template>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
  import debounce from 'lodash/debounce'

  // Utils
  import { isEmptyValue } from '@/utils/Data'

  // Constants
  import { PROFILE_TYPES } from '@/models/Profile'
  import { COUNTRY_GROUPS } from '@/store/countries'

  // Services
  import PageableApiRequest from '@/services/ApiRequests/PageableApiRequest'
  import ProfileApi from '@/services/Api/ProfileApi'

  // Model
  import AccountProfile from '@/models/AccountProfile'

  // Mixins
  import form from '@/mixins/form'

  export default {
    name: 'ProfileSelect',
    mixins: [form],
    props: {
      value: {
        type: Object,
        default: () => ({})
      },
      label: {
        type: String,
        default: 'Search'
      },
      transactionPartyType: {
        type: String,
        default: 'client'
      },
      disabled: {
        type: Boolean,
        default: false
      },
      hideIcon: {
        type: Boolean,
        default: false
      },
      clearable: {
        type: Boolean,
        default: false
      },
      searchParams: {
        type: Object,
        default: () => ({})
      },
      license: {
        type: String,
        default: null,
        required: false
      }
    },
    data () {
      return {
        search: null,
        options: [],
        searchFilters: {
          firstName: true,
          lastName: true,
          companyName: true,
          regNumber: true,
          iban: true,
          accountId: true
        },
        profilesApiRequest: new PageableApiRequest(
          ProfileApi.fetchAll, AccountProfile
        )
      }
    },
    computed: {
      iconsVisible () {
        return this.search && this.isProfileSelected && !this.clearable
      },
      isProfileSelected () {
        return this.value.id != null
      },
      residentProfileIcon () {
        if (this.isResidentProfile(this.value)) {
          return { name: 'home', tooltip: 'EU resident' }
        }

        return { name: 'language', tooltip: 'Non-EU resident' }
      },
      profileTypeIcon () {
        if (this.isLegalTypeProfile(this.value)) {
          return { name: 'work', tooltip: PROFILE_TYPES.LEGAL }
        }

        return { name: 'person', tooltip: PROFILE_TYPES.PRIVATE }
      },
      icons () {
        if (this.isProfileSelected) {
          return [
            this.profileTypeIcon,
            this.residentProfileIcon
          ]
        }

        return []
      },
      loading () {
        return this.profilesApiRequest.loading
      }
    },
    watch: {
      value: {
        handler () {
          if (this.search == null && this.value.id != null) {
            this.options = this.groupProfileOptions(this.formatProfileOptions([this.value]))
            this.search = this.value.name
          }
        },
        immediate: true
      },
      search: debounce(function (searchValue) {
        if (!searchValue || !searchValue.trim()) {
          return
        }

        this.searchProfiles(searchValue)
          .then(profiles =>
            this.license != null
              ? profiles.filter(profile => profile.license === this.license)
              : profiles
          )
          .then(profiles => {
            this.options = this.groupProfileOptions(
              this.formatProfileOptions(profiles)
            )
          })
          .catch(() => {})
      }, 300)
    },
    methods: {
      isResidentProfile (profile) {
        return profile.residence === COUNTRY_GROUPS.EU
      },
      isLegalTypeProfile (profile) {
        return profile.profileType === PROFILE_TYPES.LEGAL
      },
      searchProfiles (searchValue) {
        return this.profilesApiRequest.execute({
          search: searchValue,
          ...this.searchFilters,
          ...this.searchParams,
          size: 100,
          page: 0
        })
      },
      formatProfileOptions (profiles) {
        return profiles.reduce((options, profile) => {
          if (!options[profile.status]) {
            options[profile.status] = []
          }

          options[profile.status].push(profile)
          return options
        }, {})
      },
      groupProfileOptions (optionValues) {
        const groups = Object.keys(optionValues)
        groups.sort()

        return groups
          .reduce((options, group) => {
            return [...options, { header: group }, ...optionValues[group], { divider: true }]
          }, [])
      },
      onProfileSelect (profile) {
        this.$refs.profileSelect.blur()

        if (profile != null || this.clearable) {
          this.$emit('input', profile)
          this.$emit('change', profile)
        }
      },
      getItemText (profile) {
        return isEmptyValue(profile.name) ? profile.id : profile.name
      }
    }
  }
</script>

<style lang="scss">
  .profile-select {
    &--menu {
      .v-subheader {
        font-weight: bold;
        color: black;
      }
    }
  }
</style>
